<app-breadcrumbs title="Inicio" [breadcrumbItems]="breadCrumbItems"></app-breadcrumbs>

<!-- Loading state -->
@if (loading) {
  <div class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Cargando...</span>
    </div>
    <p class="text-muted mt-3 mb-0">Cargando panel de control...</p>
  </div>
}

@if (!loading) {

  <!-- Greeting bar -->
  <div class="row mb-4">
    <div class="col-12">
      <div class="card border-0 bg-primary bg-gradient shadow-sm">
        <div class="card-body py-3 px-4">
          <div class="d-flex align-items-center">
            <i class="ri-home-smile-2-line text-white fs-1 me-3 opacity-75"></i>
            <div>
              <h5 class="text-white fw-semibold mb-0">Bienvenido, {{ displayName }}</h5>
              <p class="text-white mb-0 opacity-75 small">Panel de Gestión SST &mdash; {{ today | date:'dd/MM/yyyy' }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- KPI Global row -->
  <div class="row row-cols-2 row-cols-sm-3 row-cols-xl-5 g-3 mb-4">
    @for (kpi of kpiCards; track kpi.label) {
      <div class="col">
        <div class="card h-100 border-0 shadow-sm">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1 me-2">
                <p class="text-muted mb-1 small fw-medium">{{ kpi.label }}</p>
                <h4 class="mb-0 fw-bold" [countUp]="kpi.value" [options]="countUpOpts"></h4>
              </div>
              <div class="avatar-sm flex-shrink-0">
                <span class="avatar-title rounded-circle bg-{{ kpi.color }}-subtle">
                  <i class="{{ kpi.icon }} text-{{ kpi.color }} fs-4"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    }
  </div>

  <!-- Row 1: Sociodemográfico + Accidentes -->
  <div class="row g-3 mb-3">

    <!-- Sociodemográfico -->
    <div class="col-xl-6">
      <div class="card h-100 border-0 shadow-sm">
        <div class="card-header bg-transparent border-0 pt-3 pb-0 px-4">
          <div class="d-flex align-items-center">
            <i class="ri-bar-chart-grouped-line text-primary me-2 fs-5"></i>
            <h6 class="card-title mb-0 fw-semibold">Perfil Sociodemográfico</h6>
          </div>
        </div>
        <div class="card-body px-4 pt-3">
          <div class="row g-2 mb-3">
            <div class="col-4">
              <div class="rounded-3 bg-success-subtle p-2 text-center">
                <p class="text-success fw-bold fs-5 mb-0" [countUp]="sociodem.ingresosMes" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Ingresos mes</small>
              </div>
            </div>
            <div class="col-4">
              <div class="rounded-3 bg-danger-subtle p-2 text-center">
                <p class="text-danger fw-bold fs-5 mb-0" [countUp]="sociodem.retirosMes" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Retiros mes</small>
              </div>
            </div>
            <div class="col-4">
              <div class="rounded-3 bg-info-subtle p-2 text-center">
                <p class="text-info fw-bold fs-5 mb-0" [countUp]="sociodem.promedioEdad" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Edad promedio</small>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-6">
              <p class="text-muted small text-center fw-medium mb-0">Por Género</p>
              <apx-chart
                [series]="chartGenero.series"
                [chart]="chartGenero.chart"
                [labels]="chartGenero.labels!"
                [colors]="chartGenero.colors!"
                [legend]="chartGenero.legend"
                [dataLabels]="chartGenero.dataLabels"
                [responsive]="chartGenero.responsive!"
              ></apx-chart>
            </div>
            <div class="col-6">
              <p class="text-muted small text-center fw-medium mb-0">Por Cargo</p>
              <apx-chart
                [series]="chartCargo.series"
                [chart]="chartCargo.chart"
                [labels]="chartCargo.labels!"
                [colors]="chartCargo.colors!"
                [legend]="chartCargo.legend"
                [dataLabels]="chartCargo.dataLabels"
                [responsive]="chartCargo.responsive!"
              ></apx-chart>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Accidentes de Trabajo -->
    <div class="col-xl-6">
      <div class="card h-100 border-0 shadow-sm">
        <div class="card-header bg-transparent border-0 pt-3 pb-0 px-4">
          <div class="d-flex align-items-center">
            <i class="ri-alarm-warning-line text-danger me-2 fs-5"></i>
            <h6 class="card-title mb-0 fw-semibold">Accidentes de Trabajo</h6>
          </div>
        </div>
        <div class="card-body px-4 pt-3">
          <div class="row g-2 mb-3">
            <div class="col-3">
              <div class="rounded-3 bg-danger-subtle p-2 text-center">
                <p class="text-danger fw-bold fs-5 mb-0" [countUp]="accidentes.totalAnioActual" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Total año</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-warning-subtle p-2 text-center">
                <p class="text-warning fw-bold fs-5 mb-0" [countUp]="accidentes.pendientesCierre" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Pend. cierre</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-secondary-subtle p-2 text-center">
                <p class="text-secondary fw-bold fs-5 mb-0" [countUp]="accidentes.sinInvestigar" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Sin investigar</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-primary-subtle p-2 text-center">
                <p class="text-primary fw-bold fs-5 mb-0" [countUp]="accidentes.diasCargadosAnio" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Días cargados</small>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-5">
              <p class="text-muted small text-center fw-medium mb-0">Por Tipo de Evento</p>
              <apx-chart
                [series]="chartAccTipoEvento.series"
                [chart]="chartAccTipoEvento.chart"
                [labels]="chartAccTipoEvento.labels!"
                [colors]="chartAccTipoEvento.colors!"
                [legend]="chartAccTipoEvento.legend"
                [dataLabels]="chartAccTipoEvento.dataLabels"
                [responsive]="chartAccTipoEvento.responsive!"
              ></apx-chart>
            </div>
            <div class="col-7">
              <p class="text-muted small text-center fw-medium mb-0">Tendencia Mensual</p>
              <apx-chart
                [series]="chartAccTendencia.series"
                [chart]="chartAccTendencia.chart"
                [xaxis]="chartAccTendencia.xaxis"
                [colors]="chartAccTendencia.colors!"
                [stroke]="chartAccTendencia.stroke"
                [dataLabels]="chartAccTendencia.dataLabels"
                [tooltip]="chartAccTendencia.tooltip"
              ></apx-chart>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>

  <!-- Row 2: Plan de Trabajo + Plan de Capacitación -->
  <div class="row g-3 mb-3">

    <!-- Plan de Trabajo -->
    <div class="col-xl-6">
      <div class="card h-100 border-0 shadow-sm">
        <div class="card-header bg-transparent border-0 pt-3 pb-0 px-4">
          <div class="d-flex align-items-center">
            <i class="ri-task-line text-success me-2 fs-5"></i>
            <h6 class="card-title mb-0 fw-semibold">Plan de Trabajo</h6>
          </div>
        </div>
        <div class="card-body px-4 pt-3">
          <div class="row g-2 mb-3">
            <div class="col-3">
              <div class="rounded-3 bg-success-subtle p-2 text-center">
                <p class="text-success fw-bold fs-5 mb-0" [countUp]="planTrabajo.planesActivos" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Planes activos</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-primary-subtle p-2 text-center">
                <p class="text-primary fw-bold fs-5 mb-0" [countUp]="planTrabajo.avancePromedio" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">% Avance</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-danger-subtle p-2 text-center">
                <p class="text-danger fw-bold fs-5 mb-0" [countUp]="planTrabajo.tareasVencidas" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Tareas venc.</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-warning-subtle p-2 text-center">
                <p class="text-warning fw-bold fs-5 mb-0" [countUp]="planTrabajo.actividadesSinIniciar" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Sin iniciar</small>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-5">
              <p class="text-muted small text-center fw-medium mb-0">Estado Actividades</p>
              <apx-chart
                [series]="chartPtEstado.series"
                [chart]="chartPtEstado.chart"
                [labels]="chartPtEstado.labels!"
                [colors]="chartPtEstado.colors!"
                [legend]="chartPtEstado.legend"
                [dataLabels]="chartPtEstado.dataLabels"
                [responsive]="chartPtEstado.responsive!"
              ></apx-chart>
            </div>
            <div class="col-7">
              <p class="text-muted small text-center fw-medium mb-1">Avance por Plan</p>
              @for (plan of planTrabajo.avancePorPlan.slice(0, 5); track plan.titulo) {
                <div class="mb-2">
                  <div class="d-flex justify-content-between mb-1">
                    <small class="text-truncate text-muted plan-label" [title]="plan.titulo">{{ plan.titulo }}</small>
                    <small class="fw-semibold">{{ plan.avance }}%</small>
                  </div>
                  <div class="progress progress-thin">
                    <div class="progress-bar bg-success" [style.width.%]="plan.avance"></div>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Plan de Capacitación -->
    <div class="col-xl-6">
      <div class="card h-100 border-0 shadow-sm">
        <div class="card-header bg-transparent border-0 pt-3 pb-0 px-4">
          <div class="d-flex align-items-center">
            <i class="ri-graduation-cap-line text-warning me-2 fs-5"></i>
            <h6 class="card-title mb-0 fw-semibold">Plan de Capacitación</h6>
          </div>
        </div>
        <div class="card-body px-4 pt-3">
          <div class="row g-2 mb-3">
            <div class="col-3">
              <div class="rounded-3 bg-warning-subtle p-2 text-center">
                <p class="text-warning fw-bold fs-5 mb-0" [countUp]="planCap.planesVigentes" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Planes vigentes</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-primary-subtle p-2 text-center">
                <p class="text-primary fw-bold fs-5 mb-0" [countUp]="planCap.temasProgramados" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Temas prog.</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-success-subtle p-2 text-center">
                <p class="text-success fw-bold fs-5 mb-0" [countUp]="planCap.porcentajeCumplimientoCursosPeriodicos" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">% Cumplimiento</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-danger-subtle p-2 text-center">
                <p class="text-danger fw-bold fs-5 mb-0" [countUp]="planCap.convocadosSinAsistencia" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Sin asistencia</small>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-5">
              <p class="text-muted small text-center fw-medium mb-0">Cursos Periódicos</p>
              <apx-chart
                [series]="chartPcCursos.series"
                [chart]="chartPcCursos.chart"
                [labels]="chartPcCursos.labels!"
                [colors]="chartPcCursos.colors!"
                [legend]="chartPcCursos.legend"
                [dataLabels]="chartPcCursos.dataLabels"
                [responsive]="chartPcCursos.responsive!"
              ></apx-chart>
            </div>
            <div class="col-7">
              <p class="text-muted small text-center fw-medium mb-0">Cumplimiento por Categoría</p>
              <apx-chart
                [series]="chartPcCategoria.series"
                [chart]="chartPcCategoria.chart"
                [xaxis]="chartPcCategoria.xaxis"
                [colors]="chartPcCategoria.colors!"
                [dataLabels]="chartPcCategoria.dataLabels"
                [plotOptions]="chartPcCategoria.plotOptions"
              ></apx-chart>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>

  <!-- Row 3: Enfermedad Laboral + Casos Médicos -->
  <div class="row g-3 mb-4">

    <!-- Enfermedad Laboral -->
    <div class="col-xl-6">
      <div class="card h-100 border-0 shadow-sm">
        <div class="card-header bg-transparent border-0 pt-3 pb-0 px-4">
          <div class="d-flex align-items-center">
            <i class="ri-heart-pulse-line text-purple me-2 fs-5"></i>
            <h6 class="card-title mb-0 fw-semibold">Enfermedad Laboral</h6>
          </div>
        </div>
        <div class="card-body px-4 pt-3">
          <div class="row g-2 mb-3">
            <div class="col-3">
              <div class="rounded-3 bg-danger-subtle p-2 text-center">
                <p class="text-danger fw-bold fs-5 mb-0" [countUp]="enfLaboral.totalAnioActual" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Total año</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-warning-subtle p-2 text-center">
                <p class="text-warning fw-bold fs-5 mb-0" [countUp]="enfLaboral.enCalificacion" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">En calificación</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-primary-subtle p-2 text-center">
                <p class="text-primary fw-bold fs-5 mb-0" [countUp]="enfLaboral.conPclDefinitivo" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">PCL definitivo</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-info-subtle p-2 text-center">
                <p class="text-info fw-bold fs-5 mb-0" [countUp]="enfLaboral.promedioPcl" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">% PCL prom.</small>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-5">
              <p class="text-muted small text-center fw-medium mb-0">Por Estado</p>
              <apx-chart
                [series]="chartElEstado.series"
                [chart]="chartElEstado.chart"
                [labels]="chartElEstado.labels!"
                [colors]="chartElEstado.colors!"
                [legend]="chartElEstado.legend"
                [dataLabels]="chartElEstado.dataLabels"
                [responsive]="chartElEstado.responsive!"
              ></apx-chart>
            </div>
            <div class="col-7">
              <p class="text-muted small text-center fw-medium mb-0">Tendencia Mensual</p>
              <apx-chart
                [series]="chartElTendencia.series"
                [chart]="chartElTendencia.chart"
                [xaxis]="chartElTendencia.xaxis"
                [colors]="chartElTendencia.colors!"
                [stroke]="chartElTendencia.stroke"
                [dataLabels]="chartElTendencia.dataLabels"
                [tooltip]="chartElTendencia.tooltip"
              ></apx-chart>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Seguimiento Casos Médicos -->
    <div class="col-xl-6">
      <div class="card h-100 border-0 shadow-sm">
        <div class="card-header bg-transparent border-0 pt-3 pb-0 px-4">
          <div class="d-flex align-items-center">
            <i class="ri-hospital-line text-info me-2 fs-5"></i>
            <h6 class="card-title mb-0 fw-semibold">Seguimiento Casos Médicos</h6>
          </div>
        </div>
        <div class="card-body px-4 pt-3">
          <div class="row g-2 mb-3">
            <div class="col-3">
              <div class="rounded-3 bg-info-subtle p-2 text-center">
                <p class="text-info fw-bold fs-5 mb-0" [countUp]="casosMedicos.casosActivos" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Casos activos</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-danger-subtle p-2 text-center">
                <p class="text-danger fw-bold fs-5 mb-0" [countUp]="casosMedicos.diasIncapacidadAnio" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Días incapac.</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-warning-subtle p-2 text-center">
                <p class="text-warning fw-bold fs-5 mb-0" [countUp]="casosMedicos.pendientesReincorporacion" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Pend. reincorp.</small>
              </div>
            </div>
            <div class="col-3">
              <div class="rounded-3 bg-success-subtle p-2 text-center">
                <p class="text-success fw-bold fs-5 mb-0" [countUp]="casosMedicos.casosCerradosMes" [options]="countUpOptsFast"></p>
                <small class="text-muted d-block">Cerrados mes</small>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-5">
              <p class="text-muted small text-center fw-medium mb-0">Por Origen</p>
              <apx-chart
                [series]="chartCmOrigen.series"
                [chart]="chartCmOrigen.chart"
                [labels]="chartCmOrigen.labels!"
                [colors]="chartCmOrigen.colors!"
                [legend]="chartCmOrigen.legend"
                [dataLabels]="chartCmOrigen.dataLabels"
                [responsive]="chartCmOrigen.responsive!"
              ></apx-chart>
            </div>
            <div class="col-7">
              <p class="text-muted small text-center fw-medium mb-0">Días de Incapacidad</p>
              <apx-chart
                [series]="chartCmTendencia.series"
                [chart]="chartCmTendencia.chart"
                [xaxis]="chartCmTendencia.xaxis"
                [colors]="chartCmTendencia.colors!"
                [stroke]="chartCmTendencia.stroke"
                [fill]="chartCmTendencia.fill"
                [dataLabels]="chartCmTendencia.dataLabels"
              ></apx-chart>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>

}
